import React, { useMemo } from "react";
import { Modal } from "@pd/pd-ui-kit";
import { add, endOfDay, format, startOfDay } from "date-fns";
import { useTranslation } from "react-i18next";

import Preloader from "~/components/Preloader";
import { useGetLocationData, useSetLocationData } from "~/hooks/locationData";
import {
  useChangePeriodMutation,
  useCreateAccountingMutation,
} from "~/schemas/generated/graphql";
import { useNotify } from "~/shared/hooks/useNotify";

import AccountingPeriodForm, {
  getQuarterEndDate,
  IAccountingPeriodForm,
} from "./AccountingPeriodForm";

const accountingPeriodFormId = "AccountingPeriodForm";

const DATE_TIME_FORMAT = "yyyy-MM-dd'T'HH:mm:ss.SSSxxx";

enum ModalState {
  edit = "Edit",
  create = "Create",
}

interface ISelectedPeriod {
  id: string;
  Year: number;
  Quarter: number;
  reportDateRaw: number;
  lastDateRaw: number;
}

interface AccountingPeriodModalProps {
  selectedPeriod?: ISelectedPeriod;
  reFetchTable?: () => void;
}

const AccountingPeriodModal = ({
  selectedPeriod,
  reFetchTable,
}: AccountingPeriodModalProps) => {
  const { t } = useTranslation();
  const setDataReplace = useSetLocationData(true);
  const { openModal, currentId } = useGetLocationData();

  const modalIsOpen =
    openModal === ModalState.create || openModal === ModalState.edit;
  const isEdit = openModal === ModalState.edit && !!currentId;

  const { notifyError } = useNotify();

  const handleCloseModal = () =>
    setDataReplace("openModal", "", "currentId", "");

  const onCompletedHandler = () => {
    reFetchTable?.();
    handleCloseModal();
  };

  const onErrorHandler = ({ graphQLErrors }) => {
    notifyError(
      ` ${t(`errors.${graphQLErrors[0]["extensions"]["code"]}_period`)}`,
    );
  };

  const [createReport] = useCreateAccountingMutation({
    onCompleted: onCompletedHandler,
    onError: onErrorHandler,
  });
  const [changePeriod] = useChangePeriodMutation({
    onCompleted: onCompletedHandler,
    onError: onErrorHandler,
  });

  const createDefaultValues: IAccountingPeriodForm = useMemo(() => {
    const quarterEndDate = getQuarterEndDate(new Date().getFullYear(), 1);
    return {
      Year: new Date().getFullYear(),
      Quarter: 1,
      reportDate: quarterEndDate,
      lastDate: quarterEndDate,
    };
  }, []);

  const editDefaultValues: IAccountingPeriodForm | undefined = useMemo(
    () =>
      selectedPeriod
        ? {
            Year: selectedPeriod.Year,
            Quarter: selectedPeriod.Quarter,
            reportDate: selectedPeriod.reportDateRaw
              ? new Date(selectedPeriod.reportDateRaw)
              : null,
            lastDate: selectedPeriod.lastDateRaw
              ? new Date(selectedPeriod.lastDateRaw)
              : null,
          }
        : undefined,
    [selectedPeriod],
  );

  const onSubmit = (formData: IAccountingPeriodForm) => {
    if (!formData.reportDate || !formData.lastDate) return;

    const periodDates = {
      lastDate: format(endOfDay(new Date(formData.lastDate)), DATE_TIME_FORMAT),
      reportDate: format(
        add(startOfDay(new Date(formData.reportDate)), { hours: 4 }),
        DATE_TIME_FORMAT,
      ),
    };

    isEdit
      ? changePeriod({
          variables: {
            PeriodId: currentId,
            ...periodDates,
          },
        })
      : createReport({
          variables: {
            Year: formData.Year,
            Quarter: formData.Quarter,
            ...periodDates,
          },
        });
  };

  return (
    <Modal
      open={modalIsOpen}
      title={t("accounting.periodAcc")}
      onCancel={handleCloseModal}
      closable
      centered
      destroyOnHidden
      okButton={{
        htmlType: "submit",
        form: accountingPeriodFormId,
        children: t("ui.save"),
      }}
    >
      {isEdit ? (
        editDefaultValues ? (
          <AccountingPeriodForm
            formId={accountingPeriodFormId}
            isEdit
            defaultValues={editDefaultValues}
            onSubmitHandler={onSubmit}
          />
        ) : (
          <Preloader />
        )
      ) : (
        <AccountingPeriodForm
          formId={accountingPeriodFormId}
          isEdit={false}
          defaultValues={createDefaultValues}
          onSubmitHandler={onSubmit}
        />
      )}
    </Modal>
  );
};

export default AccountingPeriodModal;
